import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import styled, { css } from "styled-components";

import Dropdown from "components/atoms/Dropdown";
import HtmlHead from "components/atoms/HtmlHead";
import Icon from "components/atoms/Icon";
import Tooltip from "components/atoms/Tooltip";
import Alert from "components/atoms/alert/Alert";
import Button, { BUTTON_STYLES, ToggleIconButton } from "components/atoms/button";
import Checkbox from "components/atoms/input/Checkbox";
import SelectBox from "components/atoms/input/SelectBox";
import Text from "components/atoms/text/Text";
import useToast from "components/atoms/toast/useToast";

import { BodyWithBreadcrumb, Breadcrumb, BreadcrumbTitle } from "components/molecules/Breadcrumb";
import Table from "components/molecules/table/Table";
import TableColumnVisibilityMenu from "components/molecules/table/TableColumnVisibilityMenu";
import {
    compareColumns,
    getColumnOrder,
    getColumnSize,
    getColumnVisibility,
    updateColumnOrder,
} from "components/molecules/table/TableManager";
import PaperDetailModal from "components/page/modal/PaperDetailModal";
import ReferenceCitationMultiAddModal from "components/page/modal/ReferenceCitationMultiAddModal";
import ReferenceSummaryModal from "components/page/modal/ReferenceSummaryModal";
import SearchSyntaxGuideModal from "components/page/modal/SearchSyntaxGuideModal";
import LoginUserLayout from "components/templates/LoginUserLayout";

import { useBibliographyBulkCreate } from "hooks/queries/useBibliographies";
import { useDeletePaperSearchHistory, usePaperSearch, usePaperSearchHistory } from "hooks/queries/usePapers";
import { useUser } from "hooks/queries/useUser";
import { useStableQueryData } from "hooks/useStableQueryData";

import { useDevice } from "utils/device";
import { toBibJson } from "utils/scienceon";
import { isReadonlyUser } from "utils/user";
import { genericEnterKeyDown } from "utils/validation";

const SearchInputWrap = styled.div`
    display: flex;
    align-items: end;
    padding: 0 0 0 16px;
    flex: 1;
    height: 100%;
`;

const SearchMaxWidth = styled.div`
    display: flex;
    align-items: center;
    flex: 1;
`;

const RefPoint = styled.div`
    position: relative;
    flex: 1;
    min-width: 100%;
    height: 34px;
`;

const InputWrap = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    width: 80%;
    height: auto;
    background-color: var(--color-White);
    border-radius: 8px;
    border: solid 1px var(--color-Line);
    transition: all 120ms ease-in;
    z-index: 4;

    &:focus-within {
        border: solid 1px var(--color-Key);
    }
`;

const RowWrap = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 8px;
`;

const SearchInput = styled.input`
    padding: 12px 4px;
    width: calc(100% - 10px);
    height: 32px;
    font-size: 14px;
    font-weight: 400;
    background-color: var(--color-White);
    color: var(--color-Black);
    border: transparent;
    outline: none;

    &::placeholder {
        color: var(--color-Grey2);
    }
`;

const SearchButtonWrap = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 4px;
`;

const SearchHistoryList = styled.div`
    flex-direction: column;
    margin: 0 8px;
    padding: 8px 0 4px 0;
    width: calc(100% - 16px);
    background-color: var(--color-White);
    border-top: solid 1px var(--color-Grey3);
`;

const SearchHistoryItem = styled.div`
    display: flex;
    align-items: center;
    padding: 2px 0;
    width: 100%;
    height: 40px;
    font-size: 14px;
    font-weight: 500;
    color: var(--color-Black);
    border: transparent;
    background-color: var(--color-White);

    &:hover {
        > div {
            background-color: var(--color-ButtonHover4);
        }
    }
`;

const ItemInner = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 8px;
    width: 100%;
    height: 100%;
    background-color: var(--color-White);
    border-radius: 4px;
    border: transparent;

    &:first-child {
        margin: 4px 0 0 0;
    }

    &:last-child {
        margin: 0 0 4px 0;
    }
`;

const InnerLeft = styled.div`
    display: flex;
    align-items: center;
`;

const SubHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 0;
    width: 100%;
    flex-shrink: 0;
`;

const HeaderLeftSection = styled.div`
    display: flex;
    align-items: center;
    margin-left: 10px;
`;

const SelectBoxSection = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-right: 8px;
`;

const BlankView = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
`;

const DOIWrap = styled.div`
    display: flex;
    align-items: center;

    a {
        margin: 4px 4px 0 0;

        &:last-child {
            margin: 4px 0px 0 0;
        }
    }
`;

const AddInfoSection = styled.div`
    display: flex;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 24px;
`;

const AddInfoBox = styled.div`
    flex: 1;
    display: flex;
    justify-content: space-between;
    padding: 16px;
    width: 100%;
    border-radius: 8px;
    background-color: var(--color-InfoBox);

    ${(props) =>
        props.$device.isPC &&
        css`
            padding: 24px;
        `};
`;

const AddInfoItem = styled.div`
    width: 50%;
    max-width: calc(50% - 8px);
`;

const AddInfoItemRow = styled.div`
    display: flex;
    align-items: center;
    margin: 0 0 8px 6px;
    width: 100%;

    &:last-child {
        margin: 0 0 0 6px;
    }
`;

const TextSection = styled.div`
    flex: 1;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const DivideLine = styled.span`
    display: block;
    margin: 0 12px;
    width: 1px;
    height: 16px;
    background-color: var(--color-Outline);
`;

const CheckIconStyle = {
    name: "check",
    size: "14",
    color: "var(--color-DarkKey)",
};

const ItemTextStyle = {
    margin: "0 0 0 8px",
    variant: "body-lg-400",
    color: "var(--color-SubBlack)",
};

const EMPTY_PAPER_LIST = {
    results: [],
    count: 0,
};

function SearchPage(props) {
    const device = useDevice();

    const [searchParams] = useSearchParams();

    const [searchInput, setSearchInput] = useState(searchParams.get("q") || "");

    const [searchParameter, setSearchParameter] = useState({
        "search-query": searchParams.get("q") || "",
        sort: "NULL",
        page_size: 10,
        page_index: 1,
    });

    const [displaySearchHistory, setDisplaySearchHistory] = useState("none");
    const [searchHistory, setSearchHistory] = useState(JSON.parse(localStorage.getItem("searchHistory")) || []);

    const [optionBoxActive, setOptionBoxActive] = useState(false);

    const [summaryModal, setSummaryModal] = useState(false);
    const [searchSyntaxGuideModal, setSearchSyntaxGuideModal] = useState(false);

    const [researchSelectModal, setResearchSelectModal] = useState(false);

    const [detailModal, setDetailModal] = useState(false);

    const userQuery = useUser();
    const paperSearchQuery = usePaperSearch(searchParameter);
    const {
        data: paperList,
        isLoading: isPaperListLoading,
        isRefreshing: isPaperListRefreshing,
    } = useStableQueryData(paperSearchQuery, EMPTY_PAPER_LIST);
    const paperBulkCreateMutation = useBibliographyBulkCreate();
    const user = useMemo(() => userQuery.data || {}, [userQuery.data]);

    const paperSearchHistoryQuery = usePaperSearchHistory();
    const paperSearchHistoryDeleteMutation = useDeletePaperSearchHistory();

    const searchInputRef = useRef(null);
    const { setToast } = useToast();

    const PAGE_KEY = "searchPaper";

    useEffect(() => {
        if (searchInputRef.current) {
            searchInputRef.current.blur();
        }
        const searchQuery = searchParameter["search-query"];
        if (searchQuery) {
            setSearchHistory((prevSearchHistory) => {
                let tempSearchHistory = prevSearchHistory;
                if (prevSearchHistory.includes(searchQuery)) {
                    // 검색어를 포함하고 있다면 제거
                    tempSearchHistory = prevSearchHistory.filter((item) => item !== searchQuery);
                }
                tempSearchHistory = [searchQuery, ...tempSearchHistory];
                if (tempSearchHistory.length > 10) {
                    // 길이가 10개를 넘어가면 마지막 요소를 제거
                    tempSearchHistory = tempSearchHistory.slice(0, 10);
                }
                return tempSearchHistory;
            });
        }
    }, [searchParameter]);

    useEffect(() => {
        if (paperSearchHistoryQuery.data) {
            if (user.email) {
                setSearchHistory(paperSearchHistoryQuery.data);
            }
        }
    }, [paperSearchHistoryQuery.data, user.email]);

    useEffect(() => {
        localStorage.setItem("searchHistory", JSON.stringify(searchHistory));
    }, [searchHistory]);

    const rerenderTable = React.useReducer(() => ({}), {})[1];

    // region: 체크박스 동작
    const checkedBibsRef = useRef([]);

    const handleCheckboxAllChange = (e) => {
        // 전체 선택이면 모든 id를, 그렇지 않으면 빈 배열을 반환
        checkedBibsRef.current = e.target.checked ? paperList?.results?.map((obj) => obj.scienceon_cn) : [];
        rerenderTable();
    };

    const handleCheckboxChangeById = useCallback(
        (id) => {
            const index = checkedBibsRef.current.indexOf(id);
            if (index > -1) {
                checkedBibsRef.current = checkedBibsRef.current.filter((item) => item !== id);
            } else {
                checkedBibsRef.current = [...checkedBibsRef.current, id];
            }
            rerenderTable();
        },
        [rerenderTable],
    );

    const handleCheckboxChange = useCallback(
        (event) => {
            let scienceon_cn = event.target.id;
            handleCheckboxChangeById(scienceon_cn);
        },
        [handleCheckboxChangeById],
    );

    const addReferences = (papers) => {
        let references = [];
        for (let i = 0; i < papers.length; i++) {
            const obj = papers[i];
            const bib_json = toBibJson(obj);
            const link = {};
            link.google = "https://scholar.google.com/scholar?q=" + encodeURIComponent(obj?.title);
            if (obj.doi) link.doi = obj.doi;
            if (obj.scienceon_content_url) link.scienceon_content_url = obj.scienceon_content_url;
            if (obj.scienceon_fulltext_url) link.scienceon_fulltext_url = obj.scienceon_fulltext_url;

            references.push({
                bib_json: bib_json,
                display_name: obj.title,
                link: link,
            });
        }
        paperBulkCreateMutation.mutate(
            {
                references: references,
            },
            {
                onSuccess: (data, variables, context) => {
                    setToast("참고문헌을 등록했습니다.", "info");
                },
                onError: (error) => {
                    setToast("참고문헌 등록에 실패했습니다.", "warn");
                },
            },
        );
    };

    const handleExecuteCheckedBibsAction = (checkedBibsAction) => {
        if (isReadonlyUser(user)) {
            setToast("읽기 전용 사용자 계정은 해당 기능을 사용할 수 없습니다.", "warn");
            return;
        }
        if (checkedBibsAction === "summary") {
            if (checkedBibsRef.current.length > 10) {
                Alert("warn", "에러", "요약은 10개까지만 가능합니다.");
                return;
            }
            setSummaryModal(true);
        }
        if (checkedBibsAction === "reference") {
            addReferences(paperList.results.filter((p) => checkedBibsRef.current.includes(p.scienceon_cn)));
        }
        if (checkedBibsAction === "research") {
            setResearchSelectModal(true);
        }
    };

    const [tableConfig, setTableConfig] = useState(
        localStorage.getItem(PAGE_KEY) ? JSON.parse(localStorage.getItem(PAGE_KEY)).paper : [],
    );

    //region: Table 컬럼 선언
    const columns = useMemo(
        () => [
            {
                id: "scienceon_cn",
                accessorKey: "scienceon_cn",
                header: null,
                name: "scienceon_cn",
                size: 36,
                minSize: 36,
                maxSize: 36,
                enableResizing: false,
                content: (info) => {
                    const value = info.getValue();
                    return (
                        <Checkbox
                            id={value}
                            checked={checkedBibsRef.current?.includes(value)}
                            onChange={handleCheckboxChange}
                        />
                    );
                },
            },
            {
                id: "title",
                accessorKey: "title",
                name: "title",
                style: { justifyContent: "left" },
                ...(getColumnSize("title", tableConfig) && {
                    size: getColumnSize("title", tableConfig),
                }),
                text: (info) => {
                    return info.getValue();
                },
                content: (info) => {
                    return (
                        <DOIWrap>
                            {info.row.original?.doi && (
                                <a href={info.row.original.doi} target={"_blank"} rel="noopener noreferrer">
                                    <Button {...BUTTON_STYLES.chipAction} buttonText={"DOI"} />
                                </a>
                            )}
                            <a
                                href={
                                    "https://scholar.google.com/scholar?q=" +
                                    encodeURIComponent(info.row.original?.title)
                                }
                                target={"_blank"}
                                rel="noopener noreferrer"
                            >
                                <Button {...BUTTON_STYLES.chipAction} buttonText={"Google"} />
                            </a>
                            {info.row.original?.scienceon_content_url && (
                                <a
                                    href={info.row.original.scienceon_content_url}
                                    target={"_blank"}
                                    rel="noopener noreferrer"
                                >
                                    <Button {...BUTTON_STYLES.chipAction} buttonText={"ScienceON"} />
                                </a>
                            )}
                            {info.row.original?.scienceon_fulltext_url && (
                                <a
                                    href={info.row.original.scienceon_fulltext_url}
                                    target={"_blank"}
                                    rel="noopener noreferrer"
                                >
                                    <Button {...BUTTON_STYLES.chipAction} buttonText={"PDF"} />
                                </a>
                            )}
                        </DOIWrap>
                    );
                },
            },
            {
                id: "author",
                accessorKey: "author",
                name: "author",
                size: getColumnSize("author", tableConfig) || 80,
                text: (info) => {
                    return info.getValue();
                },
            },
            {
                id: "year",
                accessorKey: "year",
                name: "year",
                size: getColumnSize("year", tableConfig) || 60,
                text: (info) => {
                    return info.getValue();
                },
            },
            {
                id: "journal",
                accessorKey: "journal",
                name: "journal",
                size: getColumnSize("journal", tableConfig) || 100,
                text: (info) => {
                    return info.getValue();
                },
            },
            {
                id: "publisher",
                accessorKey: "publisher",
                name: "publisher",
                size: getColumnSize("publisher", tableConfig) || 100,
                text: (info) => {
                    return info.getValue();
                },
            },
            {
                id: "keyword",
                accessorKey: "keyword",
                name: "keyword",
                size: getColumnSize("keyword", tableConfig) || 100,
                text: (info) => {
                    return info.getValue();
                },
            },
            {
                id: "abstract",
                accessorKey: "abstract",
                name: "abstract",
                size: getColumnSize("abstract", tableConfig) || 100,
                text: (info) => {
                    return info.getValue();
                },
            },
        ],
        [handleCheckboxChange, tableConfig],
    );

    useEffect(() => {
        const nextConfig = compareColumns(columns, [...tableConfig]);
        const hasConfigChanged = JSON.stringify(nextConfig) !== JSON.stringify(tableConfig);

        if (hasConfigChanged) {
            setTableConfig(nextConfig);
            return;
        }

        localStorage.setItem(
            PAGE_KEY,
            JSON.stringify({
                paper: tableConfig,
            }),
        );
    }, [columns, tableConfig]);

    useEffect(() => {
        // 목록이 변경되면 checkedBibsRef를 초기화 한다.
        checkedBibsRef.current = [];
        rerenderTable();
    }, [paperList.results, rerenderTable]);

    const tableRef = useRef();

    // region: 마크업 시작
    return (
        <LoginUserLayout>
            <HtmlHead title={"학술검색"} />
            <Breadcrumb>
                <BreadcrumbTitle>학술검색</BreadcrumbTitle>
                <SearchInputWrap>
                    <SearchMaxWidth $device={device}>
                        <RefPoint>
                            <InputWrap>
                                <RowWrap>
                                    <SearchInput
                                        ref={searchInputRef}
                                        placeholder="검색식을 입력해주세요"
                                        value={searchInput}
                                        onChange={(e) => {
                                            // 수정금지: 랜딩페이지에서 전달되는 쿼리 처리를 위해 searchQuery 와 searchInput 모두 필요
                                            setSearchInput(e.target.value);
                                        }}
                                        onKeyDown={(e) => {
                                            genericEnterKeyDown(e, () => {
                                                if (searchInput) {
                                                    setSearchParameter({
                                                        ...searchParameter,
                                                        "search-query": searchInput,
                                                        page_index: 1,
                                                    });
                                                }
                                            });
                                        }}
                                        onFocus={() => {
                                            setDisplaySearchHistory("flex");
                                        }}
                                        onBlur={() => {
                                            setDisplaySearchHistory("none");
                                        }}
                                    />
                                    <SearchButtonWrap>
                                        {searchInput && (
                                            <Tooltip message={"초기화"}>
                                                <Button
                                                    {...BUTTON_STYLES.iconGhost}
                                                    onClick={(e) => {
                                                        setSearchInput("");
                                                        setSearchParameter({
                                                            ...searchParameter,
                                                            "search-query": "",
                                                            page_index: 1,
                                                        });
                                                    }}
                                                >
                                                    <Icon name={"x"} color={"var(--color-SubBlack)"} />
                                                </Button>
                                            </Tooltip>
                                        )}
                                        <Tooltip message={"검색"}>
                                            <Button
                                                {...BUTTON_STYLES.iconGhost}
                                                onClick={(e) =>
                                                    setSearchParameter({
                                                        ...searchParameter,
                                                        "search-query": searchInput,
                                                        page_index: 1,
                                                    })
                                                }
                                            >
                                                <Icon name={"search"} size={"12"} color={"var(--color-SubBlack)"} />
                                            </Button>
                                        </Tooltip>
                                        <Tooltip message={"검색식 규칙"}>
                                            <Button
                                                {...BUTTON_STYLES.modalSecondary}
                                                width={"80px"}
                                                minWidth={"fit-content"}
                                                height={"24px"}
                                                padding={"0 8px"}
                                                fontSize={"small"}
                                                buttonText={"검색식 규칙"}
                                                onClick={() => setSearchSyntaxGuideModal(true)}
                                            ></Button>
                                        </Tooltip>
                                    </SearchButtonWrap>
                                </RowWrap>
                                {searchHistory.length > 0 && (
                                    <SearchHistoryList style={{ display: displaySearchHistory }}>
                                        {searchHistory.map((item, index) => (
                                            <SearchHistoryItem
                                                key={index}
                                                onMouseDown={(e) => {
                                                    setSearchInput(item);
                                                    setSearchParameter({
                                                        ...searchParameter,
                                                        "search-query": item,
                                                        page_index: 1,
                                                    });
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                }}
                                            >
                                                <ItemInner>
                                                    <InnerLeft>
                                                        <Icon
                                                            name={"last"}
                                                            size={"14"}
                                                            color={"var(--color-SubBlack)"}
                                                        />
                                                        <Text
                                                            variant="body-lg-400"
                                                            color={"var(--color-Black)"}
                                                            margin={"0 0 0 12px"}
                                                        >
                                                            {item}
                                                        </Text>
                                                    </InnerLeft>
                                                    <Tooltip message={"삭제"}>
                                                        <Button
                                                            {...BUTTON_STYLES.iconSubtleGhost}
                                                            onMouseDown={(e) => {
                                                                setSearchHistory(
                                                                    searchHistory.filter((_, i) => i !== index),
                                                                );
                                                                paperSearchHistoryDeleteMutation.mutate({
                                                                    query: item,
                                                                });
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                            }}
                                                        >
                                                            <Icon
                                                                name={"delete"}
                                                                size={"12"}
                                                                color={"var(--color-SubBlack)"}
                                                            />
                                                        </Button>
                                                    </Tooltip>
                                                </ItemInner>
                                            </SearchHistoryItem>
                                        ))}
                                    </SearchHistoryList>
                                )}
                            </InputWrap>
                        </RefPoint>
                    </SearchMaxWidth>
                </SearchInputWrap>
            </Breadcrumb>

            <BodyWithBreadcrumb>
                {!isPaperListLoading && paperList?.results?.length > 0 && (
                    <SubHeader>
                        <HeaderLeftSection>
                            <Checkbox
                                id={"all"}
                                indeterminate={
                                    checkedBibsRef.current.length > 0 &&
                                    checkedBibsRef.current.length < paperList?.results?.length
                                }
                                checked={checkedBibsRef.current.length === paperList?.results?.length}
                                onChange={handleCheckboxAllChange}
                                margin={"0 20px 0 0"}
                            />
                            <SelectBox
                                width={"auto"}
                                height={"28px"}
                                optionWidth={"280px"}
                                optionAlign={"left"}
                                optionMaxHeight={"auto"}
                                items={[
                                    { id: "", name: "--- 액션을 선택하세요. ---" },
                                    { id: "summary", name: "선택된 항목을 요약합니다." },
                                    { id: "reference", name: "선택된 항목을 참고문헌에 추가합니다." },
                                    { id: "research", name: "선택된 항목을 연구노트에 연결합니다." },
                                ]}
                                textAlign={"left"}
                                value={""}
                                onChange={(value) => {
                                    if (checkedBibsRef.current.length === 0) {
                                        setToast("선택된 항목이 없습니다.", "warn");
                                        return;
                                    }
                                    handleExecuteCheckedBibsAction(value);
                                }}
                            />
                            {checkedBibsRef.current.length > 0 && (
                                <Text margin={"0 0 0 10px"} variant="body-sm-400" fontWeight={"300"}>
                                    {checkedBibsRef.current.length}개의 항목이 선택됨
                                </Text>
                            )}
                        </HeaderLeftSection>
                        <SelectBoxSection>
                            <a href={"https://scienceon.kisti.re.kr/"}>
                                <Text margin={"0 10px 0 0"} variant="body-sm-400" fontWeight={"300"}>
                                    Powered by ScienceON
                                </Text>
                            </a>

                            <SelectBox
                                width="120px"
                                height={"28px"}
                                maxWidth="120px"
                                optionWidth="100%"
                                optionMinWidth="120px"
                                topBottom="top"
                                reverse="180deg"
                                value={searchParameter.sort}
                                items={[
                                    { id: "NULL", name: "정확도" },
                                    { id: "pubyear", name: "발행일순" },
                                ]}
                                onChange={(value) => {
                                    setSearchParameter({
                                        ...searchParameter,
                                        sort: value,
                                        page_index: 1,
                                    });
                                }}
                            />
                            <DivideLine />
                            <Tooltip message={"테이블 항목"}>
                                <Dropdown
                                    open={optionBoxActive}
                                    onOpenChange={setOptionBoxActive}
                                    placement="bottom-end"
                                    offset={4}
                                    anchor={
                                        <ToggleIconButton
                                            active={optionBoxActive}
                                            variant="secondary"
                                            margin="0 4px 0 0"
                                            buttonSize="24px"
                                            renderIcon={(active) => (
                                                <Icon
                                                    name={"option"}
                                                    color={active ? "var(--color-DarkKey)" : "var(--color-Black)"}
                                                    size={"14px"}
                                                />
                                            )}
                                            onClick={() => {
                                                setOptionBoxActive(!optionBoxActive);
                                            }}
                                        />
                                    }
                                >
                                    <TableColumnVisibilityMenu
                                        columns={columns}
                                        tableConfig={tableConfig}
                                        setTableConfig={setTableConfig}
                                        hiddenColumnIds={["title", "scienceon_cn", "add"]}
                                    />
                                </Dropdown>
                            </Tooltip>
                        </SelectBoxSection>
                    </SubHeader>
                )}
                {isPaperListLoading ? null : paperList?.results?.length > 0 ? (
                    <Table
                        ref={tableRef}
                        data={paperList?.results.map((p) => ({ ...p, id: p.scienceon_cn })) || []}
                        columns={columns}
                        useSizingColumns={true}
                        useSortingColumns={false}
                        useOrderColumns={true}
                        pagination={{
                            pageIndex: searchParameter.page_index,
                            pageSize: searchParameter.page_size,
                            count: paperList.count,
                        }}
                        setPagination={(page) => {
                            if (Number(searchParameter.page_size) !== Number(page.pageSize)) {
                                setSearchParameter({
                                    ...searchParameter,
                                    page_size: page.pageSize,
                                    page_index: 1,
                                });
                            } else if (searchParams.page !== Number(page.pageIndex)) {
                                setSearchParameter({
                                    ...searchParameter,
                                    page_index: page.pageIndex,
                                });
                            } else {
                                return;
                            }
                        }}
                        onSizeChange={(changedColumn) => {
                            Object.keys(changedColumn).forEach((key) => {
                                setTableConfig((prev) =>
                                    prev.map((item) =>
                                        item.id === key ? { ...item, size: changedColumn[key] } : item,
                                    ),
                                );
                            });
                        }}
                        columnVisibility={getColumnVisibility(tableConfig)}
                        columnOrder={getColumnOrder(tableConfig)}
                        setColumnOrder={(oldIndex, newIndex) =>
                            setTableConfig(updateColumnOrder(tableConfig, oldIndex, newIndex))
                        }
                        onRowClick={(row) => {
                            setDetailModal(row.original);
                        }}
                        checkedRowIds={checkedBibsRef.current}
                        isRefreshing={isPaperListRefreshing}
                    />
                ) : (
                    <BlankView>
                        <TextSection>
                            <Text variant="body-lg-400">검색 결과가 없습니다.</Text>
                        </TextSection>
                        <AddInfoSection>
                            <AddInfoBox $device={device}>
                                <AddInfoItem>
                                    <Text margin={"0 0 16px 0"} variant="body-lg-400" fontWeight={"600"}>
                                        키워드를 입력하여 관련 논문을 찾아볼 수 있습니다.
                                    </Text>
                                    <AddInfoItemRow>
                                        <Icon {...CheckIconStyle} />
                                        <Text {...ItemTextStyle}>최신 연구 자료를 검색할 수 있습니다.</Text>
                                    </AddInfoItemRow>
                                    <AddInfoItemRow>
                                        <Icon {...CheckIconStyle} />
                                        <Text {...ItemTextStyle}>
                                            검색 결과는 정확도 순, 최신 순으로 정렬할 수 있습니다.
                                        </Text>
                                    </AddInfoItemRow>
                                </AddInfoItem>
                                <AddInfoItem>
                                    <Text margin={"0 0 16px 0"} variant="body-lg-400" fontWeight={"600"}>
                                        검색된 논문들을 요약하거나 내 참고문헌으로 저장할 수 있습니다.
                                    </Text>
                                    <AddInfoItemRow>
                                        <Icon {...CheckIconStyle} />
                                        <Text {...ItemTextStyle}>AI가 논문들을 요약해드립니다.</Text>
                                    </AddInfoItemRow>
                                    <AddInfoItemRow>
                                        <Icon {...CheckIconStyle} />
                                        <Text {...ItemTextStyle}>
                                            관심 있는 논문을 내 참고문헌으로 추가할 수 있습니다.
                                        </Text>
                                    </AddInfoItemRow>
                                </AddInfoItem>
                            </AddInfoBox>
                        </AddInfoSection>
                    </BlankView>
                )}
            </BodyWithBreadcrumb>
            {summaryModal && (
                <ReferenceSummaryModal
                    papers={{
                        type: "searchPapers",
                        list: checkedBibsRef.current.map((scienceon_cn) => {
                            return paperList.results.find((p) => p.scienceon_cn === scienceon_cn);
                        }),
                    }}
                    exitModal={() => setSummaryModal(false)}
                />
            )}
            {searchSyntaxGuideModal && <SearchSyntaxGuideModal exitModal={() => setSearchSyntaxGuideModal(false)} />}
            {
                /* 연구노트 연결 모달 */
                researchSelectModal && (
                    <ReferenceCitationMultiAddModal
                        exitModal={() => setResearchSelectModal(false)}
                        referenceBibJsons={checkedBibsRef.current.map((scienceon_cn) => {
                            const paper = paperList.results.find((p) => p.scienceon_cn === scienceon_cn);
                            return toBibJson(paper);
                        })}
                    />
                )
            }
            {
                /* 논문 상세 모달 */
                detailModal && (
                    <PaperDetailModal
                        exitModal={() => setDetailModal(false)}
                        paper={detailModal}
                        addReferences={addReferences}
                        isReadonly={isReadonlyUser(user)}
                    />
                )
            }
        </LoginUserLayout>
    );
}

export default SearchPage;
